import type { ReactNode } from 'react'
import { cn } from '../../lib/cn.js'

type Tone = 'info' | 'success' | 'warning' | 'danger'

const tones: Record<Tone, string> = {
  info: 'bg-accent-subtle text-accent border-accent/20',
  success: 'bg-success-subtle text-success border-success/20',
  warning: 'bg-warning-subtle text-warning border-warning/20',
  danger: 'bg-danger-subtle text-danger border-danger/20',
}

interface AlertProps {
  tone?: Tone
  children: ReactNode
  className?: string
}

/**
 * A message the user needs to notice.
 *
 * Anything that reports a failure carries `role="alert"` so assistive
 * technology announces it — a red box that only sighted users learn about is
 * half a message.
 */
export function Alert({ tone = 'info', children, className }: AlertProps) {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('rounded-md border px-3 py-2 text-sm', tones[tone], className)}
    >
      {children}
    </div>
  )
}

const badgeTones: Record<Tone, string> = {
  info: 'bg-accent-subtle text-accent',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
}

export function Badge({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        badgeTones[tone],
      )}
    >
      {children}
    </span>
  )
}
